import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  ListItemButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  LibraryBooks as LibraryBooksIcon,
  Quiz as QuizIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  EventNote as EventNoteIcon,
  EmojiEvents as EmojiEventsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const TeacherNavigation = ({ 
  open, 
  onClose, 
  collapsed = false, 
  onToggleCollapse,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Navigation menu items with grouping
  const getMenuItems = () => {
    const isParent = user?.isParent || user?.is_parent || (user?.role_name && user?.role_name.toString().toLowerCase() === 'parent');
    const isTeacher = user?.isTeacher || user?.is_teacher || (user?.role_name && user?.role_name.toString().toLowerCase() === 'teacher');

    if (isParent) {
      return [
        {
          group: 'Main',
          items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Dashboard' }
          ]
        },
        {
          group: 'Children',
          items: [
            { text: 'My Children', icon: <GroupIcon />, path: '/teacher/students', ariaLabel: 'Manage My Children' },
            { text: 'Test Library', icon: <QuizIcon />, path: '/teacher/tests', ariaLabel: 'View test library' },
            { text: 'Assignments', icon: <AssignmentIcon />, path: '/parent/assignments', ariaLabel: 'Manage Assignments' }
          ]
        },
        {
          group: 'Management',
          items: [
            { text: 'Recommendations', icon: <BarChartIcon />, path: '/analytics/teacher-wrapper', ariaLabel: 'View Recommendations' },
            { text: 'Rewards & Recognition', icon: <EmojiEventsIcon />, path: '/parent/awards', ariaLabel: 'Rewards and recognition' }
          ]
        }
      ];
    } else if (isTeacher) {
      return [
        {
          group: '',
          items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Dashboard' },
            { text: 'Student Management', icon: <GroupIcon />, path: '/teacher/students', ariaLabel: 'Open student management' },
            { text: 'Questions and Tests', icon: <LibraryBooksIcon />, path: '/teacher/questionbank', ariaLabel: 'Open questions and tests' },
            { text: 'Calendar', icon: <EventNoteIcon />, path: '/teacher/calendar', ariaLabel: 'Access Calendar' },
            { text: 'Rewards & Recognition', icon: <EmojiEventsIcon />, path: '/teacher/awards', ariaLabel: 'Rewards and recognition' },
          ]
        }
      ];
    }

    // Default menu items
    return [
      {
        group: 'Main',
        items: [
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', ariaLabel: 'Navigate to Dashboard' }
        ]
      }
    ];
  };

  const menuItems = getMenuItems();

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/teacher';
    }
    if (path === '/teacher/students') {
      return (
        location.pathname === path ||
        location.pathname.startsWith('/teacher/batches') ||
        location.pathname === '/teacher/exam-prep' ||
        location.pathname === '/teacher/assignments' ||
        location.pathname === '/teacher/analytics' ||
        location.pathname === '/analytics/teacher-wrapper'
      );
    }
    if (path === '/teacher/questionbank') {
      return location.pathname === path || location.pathname === '/teacher/tests';
    }
    if (path === '/teacher/batches') {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }
    return location.pathname === path;
  };

  const renderMenuItem = (item) => {
    const active = isActive(item.path);
    
    const menuItem = (
      <ListItemButton
        key={item.text}
        onClick={() => {
          navigate(item.path);
          if (isMobile) onClose();
        }}
        selected={active}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          minHeight: 48,
          position: 'relative',
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.16)',
            color: '#FFFFFF',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.24)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              backgroundColor: '#1976d2',
              borderRadius: '0 2px 2px 0'
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#FFFFFF',
          }
        }}
        aria-label={item.ariaLabel}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: collapsed ? 40 : 48,
            color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText 
            primary={item.text}
            primaryTypographyProps={{
              fontWeight: active ? 600 : 400,
              color: active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'
            }}
          />
        )}
      </ListItemButton>
    );

    // Add tooltip when collapsed
    if (collapsed) {
      return (
        <Tooltip 
          key={item.text}
          title={item.text} 
          placement="right"
          arrow
        >
          {menuItem}
        </Tooltip>
      );
    }

    return menuItem;
  };

  const renderMenuGroup = (group) => {
    if (collapsed) {
      return group.items.map(renderMenuItem);
    }

    return (
      <Box key={group.group}>
        {group.group && (
          <Typography
            variant="caption"
            sx={{
              px: 3,
              py: 1,
              color: 'rgba(255, 255, 255, 0.5)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: '0.75rem'
            }}
          >
            {group.group}
          </Typography>
        )}
        {group.items.map(renderMenuItem)}
      </Box>
    );
  };

  const drawerContent = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        px: 2,
        py: 1.5,
        minHeight: 64,
        borderBottom: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.12)'
      }}>
        {!collapsed && (
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              color: '#FFFFFF',
              fontSize: '1.25rem'
            }}
          >
            ThinkStyne
          </Typography>
        )}
        <IconButton 
          onClick={onToggleCollapse}
          size="small"
          sx={{ 
            ml: collapsed ? 0 : 'auto',
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              color: '#FFFFFF',
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        py: 1
      }}>
        <List sx={{ py: 0 }}>
          {menuItems.map(renderMenuGroup)}
        </List>
      </Box>

      {/* Footer removed: Profile / Change Password / Logout live in top-right avatar menu (consistent with student side) */}
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? open : true}
      onClose={onClose}
      sx={{
        width: collapsed ? collapsedDrawerWidth : drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? collapsedDrawerWidth : drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
          backgroundColor: '#1a237e',
          color: '#FFFFFF',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflow: 'hidden'
        },
      }}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default TeacherNavigation;
